import React, {useState, useEffect, useRef} from 'react';
import {Box} from "@mui/system";
import {useResizeObserver} from "../../hooks/useResizeObserver";
import EventListTable, {EventNavigation} from "../../components/AssetEventList/table";
import _ from "lodash";
import {addToUrlParams} from "@dreebit-registry/vsm-microservice-components";
import {Direction} from "../../codegen/graphql";
import {GridPaginationModel, useGridApiRef} from "@mui/x-data-grid-pro";
import ManageColumnsButton from "../../components/ManageColumnsButton";
import {InstalledBaseEventListFilterTDAs} from "../../graphql/queries/installedBaseEventListFilterTDAs";
import AssetEventDetailView from "../../components/AssetEventDetailView";
import UpdateInstalledBaseEventModal from "../../components/UpdateInstalledBaseEventModal";
import EventListExportButton from "../../components/EventListExportButton";
import {buildDynamicListFilter} from "../../utils/buildDynamicListFilter";
import {withoutChangelogFilterOptions, withVisibleEventCategories} from "../../utils/eventListFilter";
import ListWithFilterDrawer from "../../components/ListWithFilterDrawer";
import {useSyncedListParams} from "../../hooks/useSyncedListParams";
import {SELECTED_EVENT_PARAM, useSelectedEventParam} from "../../hooks/useSelectedEventParam";
import { TypeDefinitionAttribute } from "../../types"; //


export const defaultQueryFields = ["name"];

const EventsListScreen = () => {
    const apiRef = useGridApiRef();
    const manageColumnsButtonRef = useRef<HTMLButtonElement>(null);

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const [, toolbarHeight] = useResizeObserver();
    const [attributes, setAttributes] = useState<TypeDefinitionAttribute[]>([]);

    const tableHeight =
        viewportHeight
        - toolbarHeight
        - (showEnvBanner ? 40 : 0) // Test Env Banner
        - 63  // Header
        - 78; // Content padding


    useEffect(() => {
        const handleResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const {
        urlParams,
        setUrlParams,
        searchParams,
        setSearchParams,
        filterParams,
        setFilterParams,
        resetFilterParams
    } = useSyncedListParams({
        storagePrefix: "eventList",
        defaultQueryFields: defaultQueryFields,
        // The open event is not a list filter
        urlKeys: { nonFilterKeys: ["page", "pageSize", SELECTED_EVENT_PARAM] },
        // v1: discard filters persisted before the IBM-843 backend rename
        // (EventCategory.ASSET_EVENT → AUDIT_LOG) — stale values fail the list query
        filterParamsVersion: 1,
    });


    const finalFilter = React.useMemo(() => {
        return withVisibleEventCategories(buildDynamicListFilter(
            filterParams,
            attributes.filter(a => a.index !== "search")
        ));
    }, [filterParams, attributes]);

    // Clean up createEvent URL params after reading
    useEffect(() => {
        if (urlParams.get('createEvent')) {
            const newParams = new URLSearchParams(urlParams);
            newParams.delete('createEvent');
            newParams.delete('eventType');
            setUrlParams(newParams, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    const onDebouncedSearch = _.debounce((values) => {
        setSearchParams(values);
        addToUrlParams(urlParams, values, setUrlParams);
    }, 700);

    const [selectedEventId, setSelectedEventId] = useSelectedEventParam();
    const [eventNavigation, setEventNavigation] = useState<EventNavigation | null>(null);
    const [editEventId, setEditEventId] = useState<string | number | null>(null);


    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1, height: '100%', width: '100%', overflow: 'hidden' }}>
            <Box sx={{ flex: 3, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <ListWithFilterDrawer
                    query={InstalledBaseEventListFilterTDAs}
                    attributesPath='installedBaseEventList.typeDefinitionAttributeList.typeDefinitionAttributes'
                    queryVariables={{
                        typeDefinitionIndex: "INSTALLED_BASE_EVENT_LIST_FILTER",
                        options: {}
                    }}
                    urlParams={urlParams}
                    setUrlParams={setUrlParams}
                    searchParams={searchParams}
                    filterParams={filterParams}
                    setFilterParams={setFilterParams}
                    resetFilterParams={resetFilterParams}
                    onSearch={onDebouncedSearch}
                    onAttributesChange={setAttributes}
                    transformAttributes={withoutChangelogFilterOptions}
                    extraHeaderActions={
                        <>
                            <ManageColumnsButton apiRef={apiRef} buttonRef={manageColumnsButtonRef}/>
                            <EventListExportButton
                                searchParams={searchParams}
                                filter={finalFilter}
                                apiRef={apiRef}
                            />
                        </>
                    }
                >
                    <EventListTable
                        height={tableHeight}
                        urlParams={urlParams}
                        filter={finalFilter}
                        search={searchParams}
                        sort={{ insert: Direction.desc }}
                        onChangePagination={(newPaginationModel: GridPaginationModel) => {
                            if (newPaginationModel) {
                                addToUrlParams(urlParams, newPaginationModel, setUrlParams)
                            }
                        }}
                        onOpenEventDetailView={setSelectedEventId}
                        onEditEvent={(id) => setEditEventId(id)}
                        selectedEventId={selectedEventId}
                        onNavigationChange={setEventNavigation}
                        isGlobalEventList={true}
                        apiRef={apiRef}
                        panelAnchorRef={manageColumnsButtonRef}
                    />
                </ListWithFilterDrawer>
            </Box>

            {selectedEventId && (
                <AssetEventDetailView
                    eventId={selectedEventId}
                    onCloseEventDetailView={() => setSelectedEventId(null)}
                    onEditEvent={(id) => setEditEventId(id)}
                    isGlobalEventList={true}
                    navigation={eventNavigation}
                />
            )}

            {editEventId && (
                <UpdateInstalledBaseEventModal
                    eventId={String(editEventId)}
                    open={!!editEventId}
                    onClose={() => setEditEventId(null)}
                />
            )}
        </Box>
    );
};

export default EventsListScreen;